import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type CustomerAttributeGroup as CustomerAttributeGroupBase } from '~/gql/graphql'
import { type Unassigned, unassignedSymbol } from '~/utils'

type CustomerAttributeGroup = App.PickGraphQL<
  CustomerAttributeGroupBase,
  'id' | 'name'
>

export type CustomerAttributeGroupSelectorProps<
  TCustomerAttributeGroup extends CustomerAttributeGroup,
> = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  customerAttributeGroups: TCustomerAttributeGroup[]
  isNullSelectable?: boolean
  nullText?: string
  onChange?: (customerAttributeGroupId: TCustomerAttributeGroup['id']) => void
  unsetText?: string
  value: TCustomerAttributeGroup['id'] | Unassigned | null
}

export const CustomerAttributeGroupSelector = React.forwardRef(
  <TCustomerAttributeGroup extends CustomerAttributeGroup>(
    {
      customerAttributeGroups,
      isNullSelectable = false,
      nullText,
      onChange,
      unsetText,
      value: valueProp,
      ...rest
    }: CustomerAttributeGroupSelectorProps<TCustomerAttributeGroup>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const data = React.useMemo(
      () =>
        isNullSelectable
          ? [{ id: null, name: nullText }, ...customerAttributeGroups]
          : customerAttributeGroups,
      [customerAttributeGroups, isNullSelectable, nullText],
    )
    const value = React.useMemo(
      () =>
        data.find(({ id }) => id === valueProp) ?? {
          id: unassignedSymbol,
          name: unsetText,
        },
      [data, unsetText, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (customerAttributeGroup: TCustomerAttributeGroup) => {
        onChange?.(customerAttributeGroup.id)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={data}
        dataItemKey="id"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
